import { BuildingOfficeIcon } from '@heroicons/react/24/outline'
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  ScrollShadow,
} from '@heroui/react'
import {
  GlobalAggregatesDto,
  type StorageReportFilters,
  type TenantReportRowDto,
  useExportStorageReportCSV,
  useGetStorageReport,
} from '@qse/api'
import { type Column, DEFAULT_RESELLER_ID, QSE_ENTERPRISE_ID, UserRoleEnum } from '@qse/constants'
import { useUserStore } from '@qse/stores'
import {
  ErrorData,
  FilterDropdown,
  type FilterOption,
  FormikControl,
  LoadingData,
  StatusChip,
  TableComponent,
  toast,
  UsageProgressBar,
} from '@qse/ui'
import { formatNumber, formatStorageSize, useIsMobile, useURLParams } from '@qse/utils'
import type { Key } from '@react-types/shared'
import { useDebounce } from '@uidotdev/usehooks'
import { Form, Formik } from 'formik'
import {
  ChevronDownIcon,
  ChevronRightIcon,
  Clock,
  Database,
  DownloadIcon,
  UserCheck,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

const columns: Column[] = [
  { name: 'Tenant Name', uid: 'tenantName', sortable: true },
  { name: 'Type', uid: 'type' },
  { name: 'Status', uid: 'status', sortable: true },
  { name: 'Plan Name', uid: 'planName' },
  { name: 'Next Billing Cycle', uid: 'billingPeriod' },
  { name: 'Active Users', uid: 'users' },
  { name: 'Storage Usage', uid: 'storageUsage' },
  { name: 'API Usage', uid: 'apiUsage' },
  { name: 'Registration Date', uid: 'createdAt', sortable: true },
  { name: '', uid: 'actions' },
]

// Filter options
const tenantTypeOptions: FilterOption[] = [
  { key: 'all', label: 'All Types' },
  { key: 'qse-retail', label: 'QSE Retail' },
  { key: 'qse-enterprise', label: 'QSE Enterprise' },
]

const statusOptions: FilterOption[] = [
  { key: 'all', label: 'All Status' },
  { key: 'active', label: 'Active' },
  { key: 'suspended', label: 'Suspended' },
  { key: 'pending_confirmation', label: 'Pending Confirmation' },
  { key: 'incomplete', label: 'Incomplete' },
]

const StorageReportCardSkeleton = () => {
  return (
    <div className="bg-white dark:bg-[#1A1A1A] rounded-lg border border-gray-200 dark:border-[#2A2A2A] p-4 shadow relative animate-pulse">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2" />
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-1" />
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-2" />
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3 mb-1" />
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
        </div>
      </div>
      <div className="flex justify-between items-center mt-3">
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded-full w-16" />
        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-20" />
      </div>
    </div>
  )
}

const StorageReportCard = ({
  tenant,
  onView,
}: {
  tenant: TenantReportRowDto
  onView: (tenant: TenantReportRowDto) => void
}) => {
  return (
    <div
      className="bg-white dark:bg-[#1A1A1A] rounded-lg border border-gray-200 dark:border-[#2A2A2A] p-4 shadow relative cursor-pointer hover:shadow-md transition-shadow"
      role="button"
      tabIndex={0}
      onClick={() => onView(tenant)}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onView(tenant)
        }
      }}
    >
      <div className="flex justify-between items-start">
        <div className="flex-1 min-w-0">
          <div className="flex justify-between">
            <div className="flex-1 font-medium text-base truncate" title={tenant.tenantName}>
              {tenant.tenantName}
            </div>
            <Button
              isIconOnly
              className="text-gray-500 flex-shrink-0 hover:text-primary dark:text-white dark:hover:text-white dark:hover:bg-gray-700/50"
              size="sm"
              variant="light"
              onPress={() => onView(tenant)}
            >
              <ChevronRightIcon className="w-4 h-4 dark:text-white dark:group-hover:text-white" />
            </Button>
          </div>
          <div className="text-xs">ID: {tenant.id}</div>
          <div className="text-xs mt-2">{tenant.type}</div>
          <div className="text-xs mt-2">
            Users: {tenant.users} | Plan: {tenant.planName || 'N/A'}
          </div>
          <div className="text-xs flex justify-between flex-wrap mt-2">
            <div className="flex flex-col gap-2">
              <p>Storage Usage:</p>
              <UsageProgressBar
                allocated={tenant.storageUsage.allocated}
                className="max-w-40"
                limit={tenant.storageUsage.limit}
                percentage={tenant.storageUsage.percentage}
                unit="bytes"
                used={tenant.storageUsage.used}
              />
            </div>
            <div className="flex flex-col gap-2">
              <p>API Usage:</p>
              <UsageProgressBar
                allocated={tenant.apiUsage.allocated}
                className="max-w-40"
                limit={tenant.apiUsage.limit || 0}
                percentage={tenant.apiUsage.percentage}
                unit=" calls/month"
                used={tenant.apiUsage.used || 0}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center text-xs mt-3">
        <StatusChip status={tenant.status} />
        <span className="text-gray-500 dark:text-[#AFAFAF]">
          {new Date(tenant.registrationDate).toLocaleDateString()}
        </span>
      </div>
    </div>
  )
}

const StorageReport = () => {
  const { user } = useUserStore()
  const location = useLocation()
  const navigate = useNavigate()
  const isSuperAdmin = user?.userRole === UserRoleEnum.SUPER_ADMIN
  const isMobile = useIsMobile()

  // URL parameter management
  const {
    values: urlParams,
    updateParam,
    updateParams,
    clearParams,
  } = useURLParams({
    page: { key: 'page', defaultValue: 1 },
    rowsPerPage: { key: 'rowsPerPage', defaultValue: 10 },
    search: { key: 'search', defaultValue: '' },
    tenantTypeFilter: { key: 'filter', defaultValue: 'all' },
    statusFilter: { key: 'status', defaultValue: 'all' },
    sortBy: { key: 'sortBy', defaultValue: '' },
    sortOrder: { key: 'sortOrder', defaultValue: '' as 'asc' | 'desc' | '' },
  })

  const { page, rowsPerPage, search, tenantTypeFilter, statusFilter, sortBy, sortOrder } = urlParams
  const [sortTimestamp, setSortTimestamp] = useState<number>(Date.now())

  // Check if we should show the not found error state
  const showNotFoundError = location.state?.showNotFoundError
  const hasShownErrorToast = useRef(false)
  const [aggregates, setAggregates] = useState<GlobalAggregatesDto | null>(null)

  // Show toast notification when redirected due to 404 error
  useEffect(() => {
    if (showNotFoundError && !hasShownErrorToast.current) {
      hasShownErrorToast.current = true
      toast.error('Page not found', {
        description: "The page or resource you're looking for doesn't exist",
      })
    }
  }, [showNotFoundError])

  const debouncedSearch = useDebounce(search, 500)

  const resellerId = useMemo(() => {
    return tenantTypeFilter === 'qse-enterprise'
      ? QSE_ENTERPRISE_ID
      : tenantTypeFilter === 'qse-retail'
        ? DEFAULT_RESELLER_ID
        : undefined // All tenants
  }, [tenantTypeFilter])

  const filters = useMemo(
    (): StorageReportFilters & { _sortTimestamp?: number } => ({
      offset: page ? (page - 1) * rowsPerPage : undefined,
      limit: rowsPerPage,
      search: debouncedSearch || undefined,
      resellerId: resellerId,
      status:
        statusFilter === 'all'
          ? undefined
          : (statusFilter as 'active' | 'suspended' | 'pending_confirmation' | 'incomplete') ||
            undefined,
      sortBy: sortBy ? (sortBy as 'tenantName' | 'status' | 'createdAt') : undefined,
      sortOrder: sortBy && sortOrder ? (sortOrder as 'asc' | 'desc') : undefined,
      _sortTimestamp: sortTimestamp, // This ensures query key changes when sorting changes
    }),
    [
      page,
      rowsPerPage,
      debouncedSearch,
      resellerId,
      statusFilter,
      sortBy,
      sortOrder,
      sortTimestamp,
    ],
  )

  // Use real API call
  const {
    data: storageReportData,
    isLoading,
    isRefetching,
    error,
    refetch: refetchStorageReport,
  } = useGetStorageReport(filters, {
    enabled: isSuperAdmin,
  })

  // CSV export hook
  const { mutate: exportCSV, isPending: isExportingCSV } = useExportStorageReportCSV()

  // Extract data and pagination info from API response
  const data = storageReportData?.data || []
  const pageInfo = storageReportData?.pageInfo
  const totalPages = pageInfo?.totalPages || 0

  useEffect(() => {
    if (storageReportData?.aggregates) {
      setAggregates(storageReportData.aggregates)
    }
  }, [storageReportData])

  // Manual refresh function
  const handleRefresh = () => {
    refetchStorageReport()
  }

  // CSV export handler - only include search, resellerId, and status filters
  const handleExportCSV = () => {
    const csvFilters = {
      search: filters.search,
      resellerId: filters.resellerId,
      status: filters.status,
    }

    exportCSV(csvFilters)
  }

  const handlePageChange = (newPage: number) => {
    updateParam('page', newPage, false)
  }

  const handleRowsPerPageChange = (newLimit: number) => {
    updateParam('rowsPerPage', newLimit)
  }

  const handleFilterChange = (newFilter: string) => {
    updateParam('tenantTypeFilter', newFilter)
  }

  const handleStatusFilterChange = (newStatusFilter: string) => {
    updateParam('statusFilter', newStatusFilter)
  }

  const handleSearchChange = (newSearch: string) => {
    updateParam('search', newSearch)
  }

  const handleInputChange = (
    value: string,
    setFieldValue: (field: string, value: string) => void,
  ) => {
    setFieldValue('search', value)
    handleSearchChange(value)
  }

  const handleClearSearch = (setFieldValue: (field: string, value: string) => void) => {
    setFieldValue('search', '')
    handleSearchChange('')
  }

  const handleSortChange = (sortDescriptor: {
    column: Key
    direction: 'ascending' | 'descending'
  }) => {
    setSortTimestamp(Date.now()) // Update timestamp to force query refetch

    // Extract column - handle various formats
    let clickedColumn: string

    if (typeof sortDescriptor.column === 'string') {
      clickedColumn = sortDescriptor.column
    } else if (
      sortDescriptor.column &&
      typeof sortDescriptor.column === 'object' &&
      'uid' in sortDescriptor.column
    ) {
      clickedColumn = (sortDescriptor.column as any).uid
    } else {
      console.error('Invalid column in sortDescriptor:', sortDescriptor.column)

      return
    }

    const clickedDirection = sortDescriptor.direction === 'ascending' ? 'asc' : 'desc'
    const currentSortBy = sortBy
    const currentSortOrder = sortOrder

    // Implement tri-state sorting: ASC → DESC → Clear → ASC...
    if (currentSortBy === clickedColumn && currentSortBy !== '') {
      // Same column clicked - cycle through states
      if (currentSortOrder === 'desc' && clickedDirection === 'asc') {
        // DESC → Clear (HeroUI tries to cycle back to ASC, we intercept to clear)
        updateParams({
          sortBy: '',
          sortOrder: '',
        })
      } else {
        // ASC → DESC
        updateParams({
          sortBy: clickedColumn,
          sortOrder: clickedDirection,
        })
      }
    } else {
      // Different column clicked OR no sort currently active - set the clicked sort
      updateParams({
        sortBy: clickedColumn,
        sortOrder: clickedDirection,
      })
    }
  }

  const handleClearFilters = () => {
    clearParams()
  }

  // Create sort descriptor for the table component
  const sortDescriptor =
    sortBy && sortBy !== ''
      ? {
          column: sortBy,
          direction: sortOrder === 'asc' ? ('ascending' as const) : ('descending' as const),
        }
      : undefined

  const handleViewTenant = (tenant: TenantReportRowDto) => {
    // Navigate to tenant analytics page for Super Admin with lastSnapshotDate
    navigate(`/dashboard/management/tenant-analytics/${tenant.id}`, {
      state: { lastSnapshotDate: storageReportData?.aggregates?.lastSnapshotDate },
    })
  }

  const tableColumns = columns // All columns are always visible for Storage Report

  const renderCell = (data: TenantReportRowDto, columnKey: Column['uid']) => {
    const tenant = data

    switch (columnKey) {
      case 'tenantName':
        return (
          <div className="flex items-center gap-3 text-gray-900 dark:text-white">
            <div className="flex flex-col gap-1 font-light text-sm">
              <p className="text-base font-medium truncate max-w-60" title={tenant.tenantName}>
                {tenant.tenantName}
              </p>
              <p className="text-sm text-gray-500 dark:text-[#AFAFAF]">ID: {tenant.id}</p>
            </div>
          </div>
        )
      case 'type':
        return <span className="text-gray-900 dark:text-white text-sm">{tenant.type}</span>
      case 'status':
        return <StatusChip status={tenant.status} />
      case 'planName':
        return <span className="text-gray-900 dark:text-white">{tenant.planName || 'N/A'}</span>
      case 'billingPeriod':
        return (
          <span className="text-gray-900 dark:text-white text-sm">
            {tenant.billingPeriodEnd
              ? new Date(tenant.billingPeriodEnd).toLocaleDateString()
              : 'N/A'}
          </span>
        )
      case 'users':
        return (
          <span className="text-gray-900 dark:text-white font-medium">{tenant.users || 0}</span>
        )
      case 'storageUsage': {
        return (
          <UsageProgressBar
            allocated={tenant.storageUsage.allocated}
            limit={tenant.storageUsage.limit}
            percentage={tenant.storageUsage.percentage}
            unit="bytes"
            used={tenant.storageUsage.used}
          />
        )
      }
      case 'apiUsage': {
        return (
          <UsageProgressBar
            allocated={tenant.apiUsage.allocated}
            limit={tenant.apiUsage.limit || 0}
            percentage={tenant.apiUsage.percentage}
            unit=" calls/month"
            used={tenant.apiUsage.used || 0}
          />
        )
      }
      case 'createdAt':
        return (
          <span className="text-gray-900 dark:text-white text-sm">
            {tenant.registrationDate
              ? new Date(tenant.registrationDate).toLocaleDateString()
              : 'N/A'}
          </span>
        )
      case 'actions':
        return (
          <Button
            isIconOnly
            className="text-gray-500 hover:text-primary dark:text-white dark:hover:text-white dark:hover:bg-gray-700/50"
            size="sm"
            variant="light"
            onPress={() => handleViewTenant(tenant)}
          >
            <ChevronRightIcon className="w-4 h-4 dark:text-white dark:group-hover:text-white" />
          </Button>
        )
      default:
        return tenant[columnKey as keyof typeof tenant]
    }
  }

  // Global Aggregates Card Component
  const GlobalAggregatesCard = ({
    title,
    value,
    subtitle,
    icon: IconComponent,
  }: {
    title: string
    value: string | number
    subtitle?: string
    icon?: React.ComponentType<{ className?: string }>
  }) => (
    <div className="bg-white dark:bg-[#1A1A1A] rounded-lg border border-gray-200 dark:border-[#2A2A2A] p-6 shadow">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600 dark:text-[#AFAFAF] font-medium">{title}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{value}</p>
          {subtitle && <p className="text-xs text-gray-500 dark:text-[#AFAFAF] mt-1">{subtitle}</p>}
        </div>
        {IconComponent && (
          <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
            <IconComponent className="w-6 h-6 text-primary" />
          </div>
        )}
      </div>
    </div>
  )

  return (
    <section className="md:px-[30px] px-4 flex flex-col gap-5 pb-24 md:pb-20 xl:pb-0">
      {/* Header Section */}
      <div className="flex justify-between flex-wrap items-center gap-4">
        <div>
          <h1 className="font-medium text-2xl">Storage Report</h1>
          <p>View tenant usage analytics and storage metrics</p>
          {isLoading ? (
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-48 mt-1 animate-pulse" />
          ) : aggregates ? (
            <p className="text-xs text-gray-500 dark:text-[#AFAFAF] mt-1 flex flex-wrap items-center gap-1">
              <span>ℹ️ Report data reflects the last nightly snapshot generated on</span>
              <span className="font-medium">
                {new Date(aggregates.lastSnapshotDate).toLocaleString('en-US', {
                  month: '2-digit',
                  day: '2-digit',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true,
                  timeZone: 'UTC',
                })}
              </span>
            </p>
          ) : null}
        </div>

        <Button
          color="primary"
          isLoading={isRefetching}
          radius="sm"
          size="lg"
          variant="flat"
          onPress={handleRefresh}
        >
          Refresh Data
        </Button>
      </div>

      {/* Global Aggregates Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        {isLoading && !aggregates ? (
          <>
            {Array.from({ length: 4 }).map((_, index) => (
              <div
                key={index}
                className="bg-white dark:bg-[#1A1A1A] rounded-lg border border-gray-200 dark:border-[#2A2A2A] p-6 shadow animate-pulse"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2" />
                    <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-2" />
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3 mt-2" />
                  </div>
                  <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-lg" />
                </div>
              </div>
            ))}
          </>
        ) : aggregates ? (
          <>
            <GlobalAggregatesCard
              icon={BuildingOfficeIcon}
              subtitle="Currently active"
              title="Total Active Tenants"
              value={aggregates.totalActiveTenants}
            />
            <GlobalAggregatesCard
              icon={Clock}
              subtitle="Awaiting confirmation"
              title="Total Pending Tenants"
              value={aggregates.totalPendingTenants}
            />
            <GlobalAggregatesCard
              icon={UserCheck}
              subtitle="Across all tenants"
              title="Total Active Users"
              value={formatNumber(aggregates.totalActiveUsers)}
            />
            <GlobalAggregatesCard
              icon={Database}
              subtitle="Aggregate usage"
              title="Total Storage Used"
              value={formatStorageSize(aggregates.totalStorageUsed)}
            />
          </>
        ) : null}
      </div>

      {/* Search and Filter Controls */}
      <div className="flex justify-between flex-wrap items-center gap-4">
        <div />
        <div className="flex flex-wrap items-center gap-3">
          <div className="max-w-[278px]">
            <Formik key="search-form" initialValues={{ search }} onSubmit={() => {}}>
              {({ values, setFieldValue }) => (
                <Form>
                  <FormikControl
                    autoComplete="off"
                    control="text"
                    maxLength={256}
                    name="search"
                    placeholder="Search"
                    size={'lg'}
                    type="text"
                    endContent={
                      <div className="flex items-center gap-2">
                        <div className="text-xs text-gray-400 dark:text-gray-500">
                          {values.search.length}/{256}
                        </div>
                        {values.search && values.search.length > 0 && (
                          <button
                            className=" text-gray-400 dark:text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors"
                            title="Clear search"
                            type="button"
                            onClick={() => {
                              handleClearSearch(setFieldValue)
                            }}
                          >
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                d="M6 18L18 6M6 6l12 12"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                              />
                            </svg>
                          </button>
                        )}
                      </div>
                    }
                    startContent={
                      <svg
                        className="w-7 h-7"
                        fill="none"
                        height="28"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        width="28"
                      >
                        <path
                          d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                        />
                      </svg>
                    }
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      handleInputChange(e.target.value, setFieldValue)
                    }}
                  />
                </Form>
              )}
            </Formik>
          </div>
          <Button
            color="primary"
            isDisabled={isLoading || !data.length}
            isLoading={isExportingCSV}
            radius="sm"
            size="lg"
            startContent={isExportingCSV ? null : <DownloadIcon className="w-4 h-4" />}
            variant="solid"
            onPress={handleExportCSV}
          >
            Download CSV
          </Button>
          <FilterDropdown
            label="All Types"
            options={tenantTypeOptions}
            selectedKey={tenantTypeFilter}
            showClearButton={true}
            onSelectionChange={handleFilterChange}
          />
          <FilterDropdown
            label="All Status"
            options={statusOptions}
            selectedKey={statusFilter}
            showClearButton={true}
            onSelectionChange={handleStatusFilterChange}
          />
          <Button
            color="default"
            radius="sm"
            size="lg"
            variant="bordered"
            onPress={handleClearFilters}
          >
            Clear Filters
          </Button>
        </div>
      </div>

      {error ? (
        <ErrorData
          description="An unexpected error occurred while loading storage report data."
          title="Error Loading Data"
        />
      ) : isMobile ? (
        isLoading ? (
          <LoadingData
            className="h-[calc(100vh-500px)]"
            description="Please wait while we fetch your data..."
            title="Loading Storage Report"
          />
        ) : (
          <div className="">
            {isRefetching ? (
              <ScrollShadow
                hideScrollBar
                className="max-h-[calc(100vh-320px)] overflow-y-auto flex flex-col gap-4"
              >
                {Array.from({ length: rowsPerPage }).map((_, index) => (
                  <StorageReportCardSkeleton key={index} />
                ))}
              </ScrollShadow>
            ) : data.length === 0 ? (
              <div className="text-center text-gray-600 dark:text-[#AFAFAF] py-8">
                No tenants found.
              </div>
            ) : (
              <ScrollShadow
                hideScrollBar
                className="max-h-[calc(100vh-320px)] overflow-y-auto flex flex-col gap-4"
              >
                {data.map(tenant => (
                  <StorageReportCard key={tenant.id} tenant={tenant} onView={handleViewTenant} />
                ))}
              </ScrollShadow>
            )}

            {data.length > 0 && (
              <div className="flex justify-between items-center mt-4 gap-2">
                <Dropdown
                  size="sm"
                  classNames={{
                    content:
                      'bg-white dark:bg-[#343537] border border-gray-200 dark:border-[#707070]',
                  }}
                >
                  <DropdownTrigger>
                    <Button
                      endContent={<ChevronDownIcon className="text-small" />}
                      size="sm"
                      variant="light"
                    >
                      {rowsPerPage}
                    </Button>
                  </DropdownTrigger>
                  <DropdownMenu
                    aria-label="Rows per page"
                    selectedKeys={new Set([rowsPerPage.toString()])}
                    selectionMode="single"
                    onAction={key => {
                      handleRowsPerPageChange(Number(key))
                    }}
                  >
                    <DropdownItem key="10">10</DropdownItem>
                    <DropdownItem key="25">25</DropdownItem>
                    <DropdownItem key="100">100</DropdownItem>
                  </DropdownMenu>
                </Dropdown>

                {/* Mobile Pagination */}
                <div className="flex items-center gap-2">
                  <Button
                    isDisabled={page <= 1}
                    size="sm"
                    variant="flat"
                    onPress={() => handlePageChange(page - 1)}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-[#AFAFAF]">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    isDisabled={page >= totalPages}
                    size="sm"
                    variant="flat"
                    onPress={() => handlePageChange(page + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        )
      ) : (
        <div className="relative">
          <TableComponent
            columns={tableColumns}
            currentPage={page}
            data={data}
            disableLocalPagination={true}
            high="h-[calc(100vh-500px)]"
            isLoading={isLoading}
            rowsPerPage={rowsPerPage}
            selectionMode="none"
            showBottomContent={true}
            showTopContent={false}
            sortDescriptor={sortDescriptor}
            total={pageInfo?.count || 0}
            totalPages={totalPages}
            trClassName="border-b dark:border-[#2A2A2A] border-[#2A2A2A]/10 cursor-pointer hover:bg-gray-50 dark:hover:bg-[#2A2A2A]/20"
            customRenderCell={
              renderCell as unknown as (data: Column, columnKey: Column['uid']) => React.ReactNode
            }
            initialVisibleColumns={[
              'tenantName',
              'type',
              'status',
              'planName',
              'billingPeriod',
              'users',
              'storageUsage',
              'apiUsage',
              'createdAt',
              'actions',
            ]}
            onLimitChange={handleRowsPerPageChange}
            onPageChange={handlePageChange}
            onRowSelect={handleViewTenant}
            onSortChange={handleSortChange}
          />
        </div>
      )}
    </section>
  )
}

export default StorageReport
